// ============================================================================
// Field tools for the prep screen — the three things drag-and-drop alone
// cannot do:
//
//   1. Required / optional per field. Without this every placed field is
//      required, which makes the signer's "N required remaining" counter a
//      lie and blocks submission on fields nobody actually needs.
//   2. Anchor-string placement. TCS agreements vary in length and page count,
//      so a layout keyed to absolute coordinates drifts the moment someone
//      adds a paragraph. An anchor does not.
//   3. Layout templates — capture the current placements once, reuse them on
//      every subsequent agreement of that type.
// ============================================================================

import { useState } from 'react';
import {
  Anchor, Check, Layers, Loader2, Save, Trash2, Wand2,
} from 'lucide-react';
import {
  EsignTemplate, FieldRow, FieldType, TemplateField,
} from './envelope-lib';
import { FIELD_LABEL } from './signer-field';
import { Suggestion, describeSuggestions } from './field-detect';

export interface FieldToolsProps {
  selected: FieldRow | null;
  fieldCount: number;
  requiredCount: number;
  templates: EsignTemplate[];
  /** True once document text has been extracted. */
  anchorReady: boolean;
  /**
   * Why text extraction failed, if it did. 'no_text_layer' means a scanned
   * PDF. Anything else is a real error. Either way the sender is TOLD —
   * silently greying the feature out is what shipped before and it left Ben
   * clicking a dead button with no explanation.
   */
  textError: string | null;
  busy: boolean;
  disabled: boolean;
  message: string | null;
  onToggleRequired: (field: FieldRow, required: boolean) => void;
  onRemove: () => void;
  onResize: (dw: number, dh: number) => void;
  onPlaceByAnchor: (anchor: string, fieldType: FieldType, required: boolean) => void;
  onApplyLayout: (layout: TemplateField[]) => void;
  onSaveLayout: (templateId: string) => void;
  /** null = not scanned yet; [] = scanned and found nothing. */
  suggestions: Suggestion[] | null;
  onDetect: () => void;
  onAcceptSuggestions: (chosen: Suggestion[]) => void;
}

const ANCHOR_SUGGESTIONS = [
  'Authorized Signature:',
  'Signature:',
  'Client Signature:',
  'Date:',
  'Initials:',
  'Title:',
];

export function FieldTools({
  selected, fieldCount, requiredCount, templates, anchorReady, textError,
  busy, disabled,
  message, onToggleRequired, onRemove, onResize, onPlaceByAnchor,
  onApplyLayout, onSaveLayout, suggestions, onDetect, onAcceptSuggestions,
}: FieldToolsProps) {
  const [anchor, setAnchor] = useState('');
  const [anchorType, setAnchorType] = useState<FieldType>('signature');
  const [anchorRequired, setAnchorRequired] = useState(true);
  const [saveTarget, setSaveTarget] = useState('');
  const [rejected, setRejected] = useState<Set<number>>(new Set());

  const suggestionSummary = suggestions ? describeSuggestions(suggestions) : '';

  const layoutTemplates = templates.filter(
    (t) => Array.isArray(t.field_layout) && t.field_layout.length > 0,
  );

  return (
    <div className="space-y-4">
      {/* ── selected field ─────────────────────────────────────────────── */}
      <section className="rounded-md border border-tcs-border bg-tcs-raised p-3">
        <h3 className="mb-2 text-sm font-semibold text-tcs-text">
          Selected field
        </h3>
        {!selected ? (
          <p className="text-xs text-tcs-text-muted">
            Click a placed field to change whether it&rsquo;s required, resize
            it, or remove it.
          </p>
        ) : (
          <>
            <p className="text-xs text-tcs-text-muted">
              {FIELD_LABEL[selected.field_type]} · page {selected.page}
            </p>

            <label className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={selected.required}
                disabled={disabled || busy}
                onChange={(e) => onToggleRequired(selected, e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm text-tcs-text">
                Required
                {selected.required && (
                  <span className="ml-1 font-bold text-st-danger-fg" aria-hidden>*</span>
                )}
              </span>
            </label>
            <p className="-mt-1 text-[11px] text-tcs-text-muted">
              {selected.required
                ? 'The signer cannot finish until this is filled in.'
                : 'The signer can skip this one.'}
            </p>

            <div className="mt-3 flex flex-wrap gap-1.5">
              <button
                type="button"
                disabled={disabled}
                onClick={() => onResize(0.02, 0)}
                className="min-h-[36px] rounded border border-tcs-border px-2 text-xs"
              >
                Wider
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onResize(-0.02, 0)}
                className="min-h-[36px] rounded border border-tcs-border px-2 text-xs"
              >
                Narrower
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onResize(0, 0.008)}
                className="min-h-[36px] rounded border border-tcs-border px-2 text-xs"
              >
                Taller
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onResize(0, -0.008)}
                className="min-h-[36px] rounded border border-tcs-border px-2 text-xs"
              >
                Shorter
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={onRemove}
                className="inline-flex min-h-[36px] items-center gap-1 rounded border border-st-danger-border-strong px-2 text-xs text-st-danger-fg"
              >
                <Trash2 className="h-3 w-3" aria-hidden /> Remove
              </button>
            </div>
          </>
        )}
        <p className="mt-3 border-t border-tcs-border pt-2 text-[11px] text-tcs-text-muted">
          {fieldCount} field{fieldCount === 1 ? '' : 's'} placed ·{' '}
          {requiredCount} required
        </p>
      </section>

      {/* ── anchor-string placement ────────────────────────────────────── */}
      <section className="rounded-md border border-tcs-border bg-tcs-raised p-3">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-tcs-text">
          <Anchor className="h-4 w-4 text-tcs-accent-ink" aria-hidden />
          Place by text
        </h3>
        <p className="text-[11px] text-tcs-text-muted">
          Drops a field wherever this exact text appears — so the placement
          survives a document that grows a page.
        </p>

        <input
          value={anchor}
          disabled={disabled || !anchorReady}
          onChange={(e) => setAnchor(e.target.value)}
          placeholder="e.g. Authorized Signature:"
          className="mt-2 w-full rounded-md border border-tcs-border px-2 py-2 text-sm"
        />
        <div className="mt-1.5 flex flex-wrap gap-1">
          {ANCHOR_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              disabled={disabled || !anchorReady}
              onClick={() => setAnchor(s)}
              className="rounded border border-dashed border-tcs-border px-1.5 py-0.5 text-[11px] text-tcs-text-muted"
            >
              {s}
            </button>
          ))}
        </div>

        <div className="mt-2 flex gap-2">
          <select
            value={anchorType}
            disabled={disabled || !anchorReady}
            onChange={(e) => setAnchorType(e.target.value as FieldType)}
            className="min-h-[40px] flex-1 rounded-md border border-tcs-border px-2 text-sm"
          >
            {(['signature', 'initials', 'date', 'name', 'email', 'text', 'checkbox'] as FieldType[])
              .map((t) => (
                <option key={t} value={t}>{FIELD_LABEL[t]}</option>
              ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-tcs-text">
            <input
              type="checkbox"
              checked={anchorRequired}
              onChange={(e) => setAnchorRequired(e.target.checked)}
              className="h-4 w-4"
            />
            Required
          </label>
        </div>

        <button
          type="button"
          disabled={disabled || busy || !anchorReady || anchor.trim().length < 2}
          onClick={() => onPlaceByAnchor(anchor.trim(), anchorType, anchorRequired)}
          className="mt-2 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-md border border-tcs-border px-3 text-sm font-medium text-tcs-text disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Find and place
        </button>
        {!anchorReady && (
          <p className="mt-1 text-[11px] text-tcs-text-muted">
            {!textError
              ? 'Reading the document text…'
              : textError === 'no_text_layer'
                ? 'This PDF has no text layer — it is a scan or an image. Place fields by dragging instead.'
                : `Could not read the document text (${textError}). Drag-and-drop still works.`}
          </p>
        )}
        <p className="mt-1 text-[11px] text-tcs-text-muted">
          Case doesn&rsquo;t matter. Punctuation does — &ldquo;Signature:&rdquo;
          won&rsquo;t match &ldquo;Signature&rdquo;.
        </p>
      </section>

      {/* ── auto-detected fields ───────────────────────────────────────── */}
      <section className="rounded-md border border-tcs-border bg-tcs-raised p-3">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-tcs-text">
          <Wand2 className="h-4 w-4 text-tcs-accent-ink" aria-hidden />
          Suggested fields
        </h3>
        <p className="text-[11px] text-tcs-text-muted">
          Scans the document for signature blocks and proposes fields. Nothing
          is placed until you say so.
        </p>

        {!anchorReady ? (
          <p className="mt-2 rounded-md bg-tcs-bg p-2 text-xs text-tcs-text-muted">
            {!textError
              ? 'Reading the document text…'
              : textError === 'no_text_layer'
                ? 'Not available for this document — it has no text layer (a scan or an image), so there is nothing to search for signature blocks in.'
                : `Not available: the document text could not be read (${textError}).`}
          </p>
        ) : suggestions === null ? (
          <button
            type="button"
            disabled={disabled || busy}
            onClick={onDetect}
            className="mt-2 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-md border border-tcs-border px-3 text-sm font-medium text-tcs-text disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Scan the document
          </button>
        ) : suggestions.length === 0 ? (
          <p className="mt-2 rounded-md bg-tcs-bg p-2 text-xs text-tcs-text-muted">
            No signature blocks found — place fields by hand.
          </p>
        ) : (
          <>
            <p className="mt-2 text-xs font-medium text-tcs-text">
              {suggestionSummary}
            </p>
            <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto">
              {suggestions.map((s, i) => (
                <li key={`${s.page}-${s.position_y}-${i}`}>
                  <label className="flex min-h-[40px] cursor-pointer items-start gap-2 rounded-md border border-tcs-border px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={!rejected.has(i)}
                      onChange={() => setRejected((prev) => {
                        const next = new Set(prev);
                        if (next.has(i)) next.delete(i); else next.add(i);
                        return next;
                      })}
                      className="mt-0.5 h-4 w-4 shrink-0"
                    />
                    <span className="min-w-0 flex-1 text-[11px]">
                      <span className="block font-medium text-tcs-text">
                        {FIELD_LABEL[s.fieldType]}
                        {s.confidence === 'medium' && (
                          <span className="ml-1 font-normal text-st-warn-fg">
                            · less certain
                          </span>
                        )}
                      </span>
                      <span className="block truncate text-tcs-text-muted">
                        p{s.page} · after &ldquo;{s.matchedLabel}&rdquo;
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <button
              type="button"
              disabled={disabled || busy || rejected.size >= suggestions.length}
              onClick={() => onAcceptSuggestions(
                suggestions.filter((_, i) => !rejected.has(i)),
              )}
              className="mt-2 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-md bg-tcs-accent-ink px-3 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
            >
              <Check className="h-4 w-4" aria-hidden />
              Place {suggestions.length - rejected.size} field
              {suggestions.length - rejected.size === 1 ? '' : 's'}
            </button>
            <p className="mt-1 text-[11px] text-tcs-text-muted">
              They go to the highlighted recipient. Untick anything wrong —
              a misplaced field on a client contract costs more than a missed
              one.
            </p>
          </>
        )}
      </section>

      {/* ── layout templates ───────────────────────────────────────────── */}
      <section className="rounded-md border border-tcs-border bg-tcs-raised p-3">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-tcs-text">
          <Layers className="h-4 w-4 text-tcs-accent-ink" aria-hidden />
          Field layouts
        </h3>

        {layoutTemplates.length > 0 ? (
          <>
            <p className="text-[11px] text-tcs-text-muted">
              Drop a saved set of fields onto this document.
            </p>
            <div className="mt-1.5 space-y-1">
              {layoutTemplates.map((t) => (
                <button
                  key={t.template_id}
                  type="button"
                  disabled={disabled || busy}
                  onClick={() => onApplyLayout(t.field_layout ?? [])}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-tcs-border px-2 py-2 text-left text-xs text-tcs-text disabled:opacity-50"
                >
                  <span className="truncate">{t.name}</span>
                  <span className="shrink-0 text-tcs-text-muted">
                    {t.field_layout?.length} fields
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <p className="text-[11px] text-tcs-text-muted">
            No saved layouts yet. Place your fields once, then save them below.
          </p>
        )}

        {fieldCount > 0 && templates.length > 0 && (
          <div className="mt-3 border-t border-tcs-border pt-3">
            <label htmlFor="layout-target" className="block text-[11px] font-medium text-tcs-text">
              Save these {fieldCount} fields to
            </label>
            <select
              id="layout-target"
              value={saveTarget}
              disabled={disabled || busy}
              onChange={(e) => setSaveTarget(e.target.value)}
              className="mt-1 min-h-[40px] w-full rounded-md border border-tcs-border px-2 text-sm"
            >
              <option value="">Choose a template…</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.name}
                  {t.field_layout?.length ? ' (replaces existing layout)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={disabled || busy || !saveTarget}
              onClick={() => onSaveLayout(saveTarget)}
              className="mt-1.5 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-md bg-tcs-accent-ink px-3 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
            >
              <Save className="h-4 w-4" aria-hidden /> Save layout
            </button>
            {/* AFB-0075 / PR 2 — said BEFORE the save, not only after it. What
                is captured is geometry: a live envelope cannot know which boxes
                the system should prefill, so they all save as signer fields and
                the merge ones are tagged on the template. */}
            <p className="mt-1 text-[11px] text-tcs-text-muted">
              These save as <strong>signer</strong> fields. Anything the system
              should fill in — a name, an address, a start date — is tagged on
              the template itself, under &ldquo;Manage template documents&rdquo;
              on the envelope list, and a template with an untagged merge field
              cannot go in a packet.
            </p>
          </div>
        )}
      </section>

      {message && (
        <p className="flex items-start gap-1.5 rounded-md border border-tcs-border bg-tcs-bg p-2 text-xs text-tcs-text">
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-st-ok-fg" aria-hidden />
          {message}
        </p>
      )}
    </div>
  );
}
